import type { TeammateFace } from "./teammateFace";

export type HeadPose = { yaw: number; pitch: number; roll: number };
export type HeadShape = { cx: number; cy: number; rx: number; ry: number };
export type FaceFeature = { x: number; y: number; ax: number; ay: number; tone: "dark" | "light" };

type Vec = [number, number, number];

export const REST_POSE: HeadPose = { yaw: 0, pitch: 0, roll: 0 };

const OUTLINE_POINTS = 28;

function normalize([x, y, z]: Vec): Vec {
  const length = Math.hypot(x, y, z) || 1;
  return [x / length, y / length, z / length];
}

function onSurface(x: number, y: number, head: HeadShape): Vec {
  const u = (x - head.cx) / head.rx;
  const v = (y - head.cy) / head.ry;
  return normalize([u, v, Math.sqrt(Math.max(0.05, 1 - u * u - v * v))]);
}

function turn([x, y, z]: Vec, pose: HeadPose): Vec {
  const x1 = x * Math.cos(pose.yaw) + z * Math.sin(pose.yaw);
  const z1 = -x * Math.sin(pose.yaw) + z * Math.cos(pose.yaw);
  return [x1, y * Math.cos(pose.pitch) - z1 * Math.sin(pose.pitch), y * Math.sin(pose.pitch) + z1 * Math.cos(pose.pitch)];
}

function toScreen([x, y, z]: Vec, head: HeadShape, pose: HeadPose): [number, number] {
  const limb = z < 0 ? Math.hypot(x, y) || 1 : 1;
  const sx = (head.rx * x) / limb;
  const sy = (head.ry * y) / limb;
  return [
    head.cx + sx * Math.cos(pose.roll) - sy * Math.sin(pose.roll),
    head.cy + sx * Math.sin(pose.roll) + sy * Math.cos(pose.roll),
  ];
}

export function featurePath(feature: FaceFeature, head: HeadShape, pose: HeadPose): string {
  const centre = onSurface(feature.x, feature.y, head);
  if (turn(centre, pose)[2] < -0.15) return "";
  const east = normalize([centre[2], 0, -centre[0]]);
  const south: Vec = [
    centre[1] * east[2] - centre[2] * east[1],
    centre[2] * east[0] - centre[0] * east[2],
    centre[0] * east[1] - centre[1] * east[0],
  ];
  const points: string[] = [];
  for (let i = 0; i < OUTLINE_POINTS; i += 1) {
    const angle = (i / OUTLINE_POINTS) * Math.PI * 2;
    const a = (feature.ax / head.rx) * Math.cos(angle);
    const b = (feature.ay / head.ry) * Math.sin(angle);
    const point = normalize([
      centre[0] + a * east[0] + b * south[0],
      centre[1] + a * east[1] + b * south[1],
      centre[2] + a * east[2] + b * south[2],
    ]);
    const [x, y] = toScreen(turn(point, pose), head, pose);
    points.push(`${i === 0 ? "M" : "L"}${x.toFixed(2)} ${y.toFixed(2)}`);
  }
  return `${points.join("")}Z`;
}

export function faceFeatures(face: TeammateFace): FaceFeature[] {
  const eyes = [20 - face.eyeGap, 20 + face.eyeGap];
  if (face.dotEyes) {
    return eyes.flatMap((cx) => {
      const x = cx + face.lookX * 0.6;
      const y = face.eyeY + face.lookY * 0.6;
      return [
        { x, y, ax: 2.3, ay: 2.3, tone: "dark" as const },
        { x: x - 0.7, y: y - 0.8, ax: 0.7, ay: 0.7, tone: "light" as const },
      ];
    });
  }
  return [
    ...eyes.map((cx) => ({ x: cx, y: face.eyeY, ax: 3.5, ay: 4.1, tone: "light" as const })),
    ...eyes.map((cx) => ({
      x: cx + face.lookX * 1.2,
      y: face.eyeY + face.lookY * 1.2 + 0.4,
      ax: 1.9,
      ay: 1.9,
      tone: "dark" as const,
    })),
  ];
}
